import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useUrlParams from '@/utils/useUrlParams';

import type { ArtifactType } from '../service';
import { getAvailableContainerImages } from '../service';
import type { CommonImageItemType, ContainerImageListOptions, ImageListState, ImageQuery } from '../types';

export function useContainerImageListState(queryObj: ImageQuery, listOptions?: ContainerImageListOptions): ImageListState {
  const { delParam } = useUrlParams();
  const [data, setData] = useState<CommonImageItemType[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [nameFilter, setNameFilter] = useState<string | undefined>(undefined);
  /** 筛选与分页未变时仍要拉列表（刷新 / 重置） */
  const [fetchNonce, setFetchNonce] = useState(0);
  const queryObjRef = useRef(queryObj);
  queryObjRef.current = queryObj;
  /** 未选择时不传 artifactType，拉取全部制品类型 */
  const [artifactType, setArtifactType] = useState<ArtifactType | undefined>(listOptions?.artifactType);
  const lockArtifactType = !!listOptions?.lockArtifactType;
  const enabled = listOptions?.enabled !== false;
  const queryKey = useMemo(() => JSON.stringify(queryObj ?? {}), [queryObj]);

  useEffect(() => {
    setPage(1);
  }, [queryKey]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getAvailableContainerImages({
          ...queryObjRef.current,
          page,
          limit,
          ...(nameFilter ? { name: nameFilter } : {}),
          ...(artifactType ? { artifactType } : {}),
        });
        if (cancelled) return;
        if (Array.isArray(res.items)) {
          setData(res.items.map((i) => ({ ...i, key: i.id })));
        } else {
          setData([]);
        }
        setTotal(typeof res.totalItems === 'number' ? res.totalItems : 0);
      } catch (e) {
        console.log(e);
        if (!cancelled) {
          setData([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryKey, page, limit, nameFilter, fetchNonce, artifactType, enabled]);

  const refreshList = useCallback(() => {
    setFetchNonce((n) => n + 1);
  }, []);

  const onSearch = useCallback((value: Record<string, any>) => {
    setNameFilter(value?.name);
    setPage(1);
  }, []);

  const handleReset = useCallback(() => {
    delParam('imageSearch');
    setNameFilter(undefined);
    setPage(1);
    setFetchNonce((n) => n + 1);
  }, [delParam]);

  const onPageChange = useCallback((nextPage: number, nextPageSize: number) => {
    setPage(nextPage);
    setLimit(nextPageSize);
  }, []);

  const setArtifactTypeWithReset = useCallback((nextArtifactType: ArtifactType | undefined) => {
    setPage(1);
    setFetchNonce((n) => n + 1);
    setArtifactType(nextArtifactType);
  }, []);

  return {
    data,
    loading,
    page,
    limit,
    total,
    onSearch,
    handleReset,
    refreshList,
    onPageChange,
    artifactType,
    setArtifactType: lockArtifactType ? undefined : setArtifactTypeWithReset,
    lockArtifactType,
  };
}
